/**
 * Notification helpers.
 *
 * Firestore schema (matches existing rules):
 *   notifications/{id} {
 *     recipientUid: string     (firestore rules key)
 *     type: string             ('task_due' | 'note_added' | 'status_change' | ...)
 *     title: string
 *     body: string
 *     link: string             (where to navigate on click — e.g. '/listings/abc')
 *     sourceType: 'listing' | 'buyer' | 'transaction' | 'contact' | 'task'
 *     sourceId: string
 *     read: boolean
 *     createdAt: serverTimestamp
 *     senderUid?: string       (who triggered it, for filtering out self-notifications)
 *   }
 */
import { db, auth } from '../firebase';
import { addDoc, collection, serverTimestamp, writeBatch, doc, getDocs, query, where } from 'firebase/firestore';
import { withTenant, _getCurrentTenantId } from '../services/data';

// Title/body length caps per spec
const MAX_TITLE = 120;
const MAX_BODY = 200;
function clip(s, n) { return (s || '').toString().substring(0, n); }

/** The one payload shape every client-side notification write uses. `me` is the
 *  signed-in sender: RW-182's rule requires fromUid == request.auth.uid, and the
 *  bell + FCM fan-out key on recipientUid — a hand-rolled write that drifts from
 *  this (RW-1893: recipientId, no fromUid) is denied 100% of the time. */
function notificationPayload({ recipientUid, type, title, body, link, sourceType, sourceId }, me) {
  return withTenant('notifications', {
    recipientUid,
    type: type || 'info',
    title: clip(title, MAX_TITLE),
    body: clip(body, MAX_BODY),
    link: link || '',
    sourceType: sourceType || null,
    sourceId: sourceId || null,
    read: false,
    createdAt: serverTimestamp(),
    timestamp: serverTimestamp(), // legacy field also queried by App.js bell
    fromUid: me,
    senderUid: me,
  });
}

/**
 * RW-1893: the throwing twin of createNotification, for surfaces that count
 * per-recipient outcomes (TrainingAdminTab's "Remind All Overdue" batch reports
 * "Sent 2 of 3"). Same payload and the same RW-182 fromUid contract; unlike
 * createNotification it does NOT swallow the write error and does NOT skip a
 * self-recipient — a reminder aimed at the caller is still a reminder.
 * Resolves to the new doc id.
 */
export async function sendNotification({ recipientUid, type, title, body, link, sourceType, sourceId, senderUid }) {
  const me = senderUid || auth?.currentUser?.uid || null;
  if (!recipientUid) throw new Error('sendNotification: recipientUid is required');
  if (!me) throw new Error('sendNotification: no signed-in sender (fromUid)');
  const ref = await addDoc(collection(db, 'notifications'), notificationPayload({ recipientUid, type, title, body, link, sourceType, sourceId }, me));
  return ref.id;
}

export async function createNotification({ recipientUid, type, title, body, link, sourceType, sourceId, senderUid }) {
  const me = senderUid || auth?.currentUser?.uid || null;
  if (!recipientUid || recipientUid === me) return null; // don't notify yourself
  // RW-182: rules require fromUid to match request.auth.uid on client writes,
  // so a logged-in caller can't spoof notifications "from" another agent.
  if (!me) return null;
  try {
    const ref = await addDoc(collection(db, 'notifications'), notificationPayload({ recipientUid, type, title, body, link, sourceType, sourceId }, me));
    return ref.id;
  } catch (e) {
    console.warn('createNotification failed', e);
    return null;
  }
}

/** Notify a list of users (skips current user, dedupes). */
export async function notifyUsers(userIds, payload) {
  const me = payload.senderUid || auth?.currentUser?.uid || null;
  const unique = [...new Set((userIds || []).filter(Boolean))].filter((u) => u !== me);
  if (unique.length === 0) return;
  await Promise.all(unique.map((uid) => createNotification({ ...payload, recipientUid: uid, senderUid: me })));
}

/** Notify all admins (users with role 'admin' or 'super_admin'). */
export async function notifyAdmins(payload) {
  const tenantId = _getCurrentTenantId();
  if (!tenantId) return;
  try {
    // RW-707: tenantId filter required by query-shape rule (RW-684 pattern)
    const snap = await getDocs(query(collection(db, 'users'), where('tenantId', '==', tenantId), where('role', 'in', ['admin', 'super_admin'])));
    const ids = snap.docs.map((d) => d.id);
    await notifyUsers(ids, payload);
  } catch (e) { console.warn('notifyAdmins failed', e); }
}

/** Notify all users in given offices (string or array). */
export async function notifyOffice(offices, payload) {
  const list = Array.isArray(offices) ? offices : [offices];
  if (list.length === 0) return;
  const tenantId = _getCurrentTenantId();
  if (!tenantId) return;
  try {
    // RW-707: tenantId filter required by query-shape rule (RW-684 pattern)
    const snap = await getDocs(query(collection(db, 'users'), where('tenantId', '==', tenantId), where('office', 'in', list.slice(0, 10))));
    const ids = snap.docs.map((d) => d.id);
    await notifyUsers(ids, payload);
  } catch (e) { console.warn('notifyOffice failed', e); }
}

/** Batch-create notifications for multiple recipients (e.g. team on a listing). */
export async function notifyMany(recipients, payload) {
  // RW-182: fromUid must equal request.auth.uid for the rule to allow the
  // write — fall back to the live signed-in uid when the caller didn't pass
  // an explicit senderUid.
  const me = payload.senderUid || auth?.currentUser?.uid || null;
  if (!me) return;
  const unique = [...new Set((recipients || []).filter(Boolean))]
    .filter((uid) => uid !== me);
  if (unique.length === 0) return;
  const batch = writeBatch(db);
  unique.forEach((recipientUid) => {
    const ref = doc(collection(db, 'notifications'));
    batch.set(ref, withTenant('notifications', {
      recipientUid,
      type: payload.type || 'info',
      title: payload.title || '',
      body: payload.body || '',
      link: payload.link || '',
      sourceType: payload.sourceType || null,
      sourceId: payload.sourceId || null,
      read: false,
      createdAt: serverTimestamp(),
      fromUid: me,
      senderUid: me,
    }));
  });
  try { await batch.commit(); } catch (e) { console.warn('notifyMany failed', e); }
}

// Icon + color per notification type. The first 11 entries are the original
// RW-190 set (icons/colors unchanged); the rest were added so the ~30 server
// trigger types render with a meaningful glyph instead of the gray 🔔 fallback.
export const NOTIFICATION_TYPES = {
  // ── original set (unchanged) ──
  task_due: { icon: '📋', color: '#F59E0B' },
  task_assigned: { icon: '👤', color: '#6366F1' },
  checklist_review: { icon: '✅', color: '#F59E0B' },
  checklist_approved: { icon: '✅', color: '#10B981' },
  checklist_rejected: { icon: '❌', color: '#EF4444' },
  note_added: { icon: '📝', color: '#6366F1' },
  document_uploaded: { icon: '📄', color: '#3B82F6' },
  status_change: { icon: '🔄', color: '#8B5CF6' },
  closing_reminder: { icon: '🏠', color: '#10B981' },
  mention: { icon: '@', color: '#EC4899' },
  info: { icon: '🔔', color: '#6B7280' },
  // ── deals ──
  task_overdue: { icon: '⏰', color: '#EF4444' },
  overdue_items: { icon: '⏰', color: '#EF4444' },
  buyer_match: { icon: '🤝', color: '#10B981' },
  transaction_status: { icon: '🔄', color: '#8B5CF6' },
  transaction_doc_uploaded: { icon: '📄', color: '#3B82F6' },
  listing_status_change: { icon: '🏷️', color: '#8B5CF6' },
  wire_viewed: { icon: '🔐', color: '#EF4444' },
  showing_request: { icon: '🚪', color: '#F59E0B' },
  // RW-2306: the SERVER-written twins of the two legacy client-written types.
  // Deliberately distinct names — the dead index.js triggers filter on
  // 'showing_request' / 'buyer_match'+fromUid, so reusing those would double-send.
  showing_requested: { icon: '🚪', color: '#F59E0B' },
  buyer_hand_raise: { icon: '🙋', color: '#10B981' },
  exclusivity_expired: { icon: '⌛', color: '#EF4444' },
  expiring_soon: { icon: '⌛', color: '#F59E0B' },
  // RW-2324 — server-written types that were in NEITHER registry. The bell
  // rendered them with the gray fallback and filed them under Updates.
  address_request: { icon: '🔑', color: '#F59E0B' },
  deal_status: { icon: '🔄', color: '#8B5CF6' },
  task_resolved: { icon: '✅', color: '#10B981' },
  task_sent_back: { icon: '↩️', color: '#F59E0B' }, // RW-2865
  deal_facts_changed: { icon: '🔀', color: '#8B5CF6' }, // RW-2880
  task_digest: { icon: '📋', color: '#4F7A4A' }, // RW-2866
  task_status_changed: { icon: '🔄', color: '#8B5CF6' },
  milestone_tasks_created: { icon: '📋', color: '#6366F1' },
  counter_offer_expired: { icon: '⌛', color: '#EF4444' },
  bba_expiring: { icon: '⌛', color: '#F59E0B' },
  bba_expired: { icon: '⌛', color: '#EF4444' },
  tc_request: { icon: '🆘', color: '#EF4444' },
  // ── compliance ──
  checklist_submitted: { icon: '✅', color: '#F59E0B' },
  compliance_doc_submitted: { icon: '📋', color: '#F59E0B' },
  compliance_doc_approved: { icon: '✅', color: '#10B981' },
  compliance_doc_rejected: { icon: '❌', color: '#EF4444' },
  compliance_auto_submit: { icon: '📋', color: '#5C50A1' },
  compliance_escalated: { icon: '⬆️', color: '#C44A3A' }, // RW-3125: a reviewer escalated a document
  compliance_doc_assigned: { icon: '📥', color: '#3B5BA8' }, // RW-3173: a document was assigned to you to review
  compliance_resubmit_nudge: { icon: '↩️', color: '#C89B3C' }, // RW-3125: a returned document still waits on a new version
  compliance_unsent_nudge: { icon: '📤', color: '#B7791F' }, // RW-3880: uploads not sent for review yet
  form_library_health_failed: { icon: '⚠️', color: '#C44A3A' }, // RW-3128: the nightly check turned a published form from passing to failing
  // RW-2324 — the two legacy transaction-compliance triggers + the stale-review nudge.
  compliance_submitted: { icon: '📋', color: '#F59E0B' },
  compliance_review_complete: { icon: '✅', color: '#10B981' },
  stale_review: { icon: '⏰', color: '#F59E0B' },
  // ── signatures (RW-1549 — docusignWebhook agent notifications) ──
  envelope_completed: { icon: '✍️', color: '#10B981' },
  envelope_declined: { icon: '❌', color: '#EF4444' },
  envelope_voided: { icon: '🚫', color: '#6B7280' },
  bba_signed: { icon: '🤝', color: '#10B981' },
  bba_extension_signed: { icon: '🤝', color: '#8B5CF6' },
  bba_signed_awaiting_broker: { icon: '🤝', color: '#F59E0B' }, // RW-3122: signed, waiting on the broker
  // ── social ──
  comment: { icon: '💬', color: '#6366F1' },
  task_comment: { icon: '💬', color: '#6366F1' }, // RW-2324 (taskCommentsApi fan-out)
  // RW-2276: `reaction` has been written by Feed.js since RW-190 but never had a
  // glyph; the group_* types are the invite / join lifecycle (G4, G7).
  reaction: { icon: '❤️', color: '#EE8172' },
  group_invite: { icon: '👥', color: '#5C50A1' },
  group_join_request: { icon: '🙋', color: '#5C50A1' },
  group_join_approved: { icon: '✅', color: '#10B981' },
  group_join_declined: { icon: '🚫', color: '#6B7280' },
  // RW-2324 review round 2: written by notifyMany() in Groups.js when a group
  // admin ticks "announcement" — the one type from src/lib/socialNotify.js's
  // family that was in neither map, so it rendered as the gray fallback under
  // Updates while every sibling from the same helper filed under Social.
  announcement: { icon: '📣', color: '#5C50A1' },
  message: { icon: '✉️', color: '#EC4899' },
  recognition: { icon: '🏆', color: '#F59E0B' },
  badge: { icon: '🎖️', color: '#F59E0B' },
  anniversary: { icon: '🎉', color: '#EC4899' },
  important_post: { icon: '📢', color: '#F59E0B' },
  critical_post: { icon: '🚨', color: '#EF4444' },
  post_reminder: { icon: '📢', color: '#6B7280' },
  // ── contacts (RW-1347 assign & share) ──
  contact_assigned: { icon: '👤', color: '#EE8172' },
  contact_shared: { icon: '🤝', color: '#5C50A1' },
  // ── events ──
  event_reminder: { icon: '📅', color: '#3B82F6' },
  event_rsvp: { icon: '🗓️', color: '#3B82F6' },
  // ── staff · HR ──
  staff_timeoff_request: { icon: '🌴', color: '#4F46E5' },
  staff_requisition: { icon: '📋', color: '#5C50A1' },
  staff_review_due: { icon: '⭐', color: '#C89B3C' },
  staff_doc_expiring: { icon: '📄', color: '#EE8172' },
  staff_nudge: { icon: '🔔', color: '#5C50A1' },
  // ── vendors (RW-2193 — ask distribution, approvals, review nudges) ──
  vendor_ask_new: { icon: '🔎', color: '#4F46E5' },
  vendor_ask_reply: { icon: '💬', color: '#4F46E5' },
  vendor_recommended: { icon: '🤝', color: '#3B5D44' },
  vendor_approved: { icon: '✅', color: '#10B981' },
  vendor_rejected: { icon: '❌', color: '#EF4444' },
  vendor_review_nudge: { icon: '⭐', color: '#C89B3C' },
  // ── academy (RW-2324) ──
  // The first FIVE are the inbox fan-out (academyApi.fanOutInbox /
  // academyNudgeCron.bellFanOut), which writes `academy_${row.kind}` for every
  // kind in academy.js INBOX_KINDS — no call site spells those out, so the
  // guard expands INBOX_KINDS instead. The four after them are ordinary
  // literal call sites in academyApi.js.
  academy_assigned: { icon: '🎓', color: '#5C50A1' },
  academy_class: { icon: '🎓', color: '#5C50A1' },
  academy_pod: { icon: '🎓', color: '#5C50A1' },
  academy_designation: { icon: '🎓', color: '#C89B3C' },
  academy_ce: { icon: '🎓', color: '#5C50A1' },
  academy_coach_request: { icon: '🎓', color: '#5C50A1' },
  academy_reschedule: { icon: '🎓', color: '#5C50A1' },
  academy_switch: { icon: '🎓', color: '#5C50A1' },
  academy_graduation: { icon: '🎓', color: '#C89B3C' },
  // ── onboarding + system (RW-2324) ──
  onboarding_assigned: { icon: '🧭', color: '#5C50A1' },
  onboarding_signed: { icon: '✍️', color: '#10B981' },
  onboarding_autoassign_failed: { icon: '⚠️', color: '#EF4444' },
  admin_alert: { icon: '⚠️', color: '#EF4444' },
  bug_report: { icon: '🐛', color: '#EF4444' },
  // createNotification's own fallback when a caller omits `type`.
  system: { icon: '🔔', color: '#6B7280' },
  // ── written by the CLIENT helper in this very file (RW-2324 review B1) ──
  // src/lib/notifications.js exports a createNotification of its own; these
  // four call sites were never audited because the guard only read the three
  // function trees. onboarding_nudge is the highest-volume of the whole set.
  contact_transferred: { icon: '👤', color: '#EE8172' },
  onboarding_nudge: { icon: '⏰', color: '#F59E0B' },
  onboarding_update: { icon: '🧭', color: '#5C50A1' },
  broadcast: { icon: '📢', color: '#5C50A1' },
  // Reached only once helper propagation followed an IMPORT (round 2). Both
  // are live and reachable: TeamManagement and TrainingAdminTab are wired into
  // AdminDashboard the same way (a lazyWithRetry dynamic import plus an
  // activeSection branch), and Training is an ungated item in adminNav.js's
  // Learning group, so any admin can send a course reminder.
  team_added: { icon: '👥', color: '#EE8172' },
  training_reminder: { icon: '📚', color: '#5C50A1' },
  // ── email signatures (P-RW-33) ──
  signature_cert_expiring: { icon: '⚠️', color: '#F59E0B' }, // RW-2901, staff admins
  signature_reply_reminder: { icon: '✉️', color: '#5C50A1' }, // RW-2897, agents
};

/**
 * Human-facing categories — zero jargon, the language an agent already uses.
 * Order here is the order sections render in the inbox.
 */
export const NOTIFICATION_CATEGORIES = [
  { key: 'deals', label: 'My Deals', icon: '🏠' },
  { key: 'compliance', label: 'Compliance', icon: '✅' },
  { key: 'social', label: 'Social', icon: '💬' },
  { key: 'events', label: 'Events', icon: '📅' },
  { key: 'updates', label: 'Updates', icon: '🔔' },
];

const TYPE_TO_CATEGORY = {
  task_assigned: 'deals', task_due: 'deals', task_overdue: 'deals', overdue_items: 'deals',
  closing_reminder: 'deals', buyer_match: 'deals', transaction_status: 'deals',
  transaction_doc_uploaded: 'deals', listing_status_change: 'deals', status_change: 'deals',
  note_added: 'deals', document_uploaded: 'deals', wire_viewed: 'deals', showing_request: 'deals',
  showing_requested: 'deals', buyer_hand_raise: 'deals', // RW-2306
  exclusivity_expired: 'deals', expiring_soon: 'deals',
  // RW-2324 — every one of these was written server-side and reached NOTHING
  // here, so `categorize()` fell through `|| 'updates'` and filed a deal event
  // under the Updates tab.
  address_request: 'deals', deal_status: 'deals', task_resolved: 'deals',
  task_sent_back: 'deals', // RW-2865
  deal_facts_changed: 'deals', // RW-2880
  task_digest: 'deals', // RW-2866
  task_status_changed: 'deals', milestone_tasks_created: 'deals',
  counter_offer_expired: 'deals', bba_expiring: 'deals', bba_expired: 'deals',
  contact_assigned: 'deals', contact_shared: 'deals',
  envelope_completed: 'deals', envelope_declined: 'deals', envelope_voided: 'deals',
  bba_signed: 'deals', bba_extension_signed: 'deals', bba_signed_awaiting_broker: 'deals',
  checklist_review: 'compliance', checklist_submitted: 'compliance', checklist_approved: 'compliance',
  checklist_rejected: 'compliance', compliance_doc_submitted: 'compliance',
  compliance_doc_approved: 'compliance', compliance_doc_rejected: 'compliance',
  compliance_auto_submit: 'compliance',
  compliance_escalated: 'compliance', compliance_resubmit_nudge: 'compliance', // RW-3125
  compliance_unsent_nudge: 'compliance', // RW-3880
  compliance_doc_assigned: 'compliance', // RW-3173
  form_library_health_failed: 'compliance', // RW-3128: a published form stopped passing its nightly check
  compliance_submitted: 'compliance', compliance_review_complete: 'compliance', // RW-2324
  stale_review: 'compliance', // RW-2324
  comment: 'social', message: 'social', mention: 'social', recognition: 'social', badge: 'social',
  anniversary: 'social', important_post: 'social', critical_post: 'social', post_reminder: 'social',
  reaction: 'social', group_invite: 'social', group_join_request: 'social',
  group_join_approved: 'social', group_join_declined: 'social',
  announcement: 'social', // RW-2324 — Groups.js notifyMany, stamped with fromUid
  task_comment: 'social', // RW-2324
  // RW-2324 — tc_request is a #NeedHelp FEED post fanned out to the TC team:
  // sourceType 'post', link '/feed?post=…', and it carries the author's
  // fromUid/fromName. Its structural twins vendor_ask_new / vendor_ask_reply
  // are 'social' for the same reason. 'deals' would send the reader to the
  // deals tab for a notification that deep-links to the Feed, AND would drop a
  // named colleague's ping out of isPersonToPerson()'s people-first block.
  tc_request: 'social',
  event_reminder: 'events', event_rsvp: 'events',
  staff_timeoff_request: 'updates', staff_requisition: 'updates', staff_review_due: 'updates',
  staff_doc_expiring: 'updates', staff_nudge: 'updates',
  vendor_ask_new: 'social', vendor_ask_reply: 'social', vendor_recommended: 'social',
  vendor_approved: 'updates', vendor_rejected: 'updates', vendor_review_nudge: 'deals',
  // RW-2324 — academy inbox fan-out (`academy_${kind}`, academy.js INBOX_KINDS)
  // plus the coaching/mentorship bells.
  academy_assigned: 'updates', academy_class: 'updates', academy_pod: 'updates',
  academy_designation: 'updates', academy_ce: 'updates',
  academy_coach_request: 'updates', academy_reschedule: 'updates',
  academy_switch: 'updates', academy_graduation: 'updates',
  // RW-2324 — onboarding + system. `info` already had a glyph but no category;
  // it landed on 'updates' by fallback, which is now stated rather than implied.
  onboarding_assigned: 'updates', onboarding_signed: 'updates',
  onboarding_autoassign_failed: 'updates',
  admin_alert: 'updates', bug_report: 'updates', system: 'updates', info: 'updates',
  // RW-2324 review B1 — the client-written four. contact_transferred joins its
  // siblings in Deals (contact_assigned / contact_shared are both 'deals'), so
  // a reassignment stops being the one contact event that files under Updates.
  contact_transferred: 'deals',
  // team_added is an org-MEMBERSHIP announcement ("You've been added to
  // <Team>", sourceType 'team'), which is the staff_* family's shape and so
  // 'updates'. The counter-argument, if anyone wants to move it: its link is
  // /contacts and the whole contact_* family is 'deals'.
  team_added: 'updates',
  onboarding_nudge: 'updates', onboarding_update: 'updates', broadcast: 'updates',
  training_reminder: 'updates',
  signature_cert_expiring: 'updates', // RW-2901
  signature_reply_reminder: 'updates', // RW-2897

};

/** Map a notification type to one of NOTIFICATION_CATEGORIES (default: 'updates'). */
export function categorize(type) {
  return TYPE_TO_CATEGORY[type] || 'updates';
}

/**
 * Thread/source key for grouping + muting. Notifications that share a source
 * (e.g. every update about transaction "xyz") collapse into one thread and can
 * be muted together. Returns null when the notification has no source, in which
 * case it stands alone and offers no mute.
 */
export function getThreadKey(n) {
  if (n && n.sourceType && n.sourceId) return `${n.sourceType}:${n.sourceId}`;
  return null;
}

/** Group key — falls back to the doc id so source-less notifications never merge. */
export function getNotificationGroupKey(n) {
  return getThreadKey(n) || `solo:${n.id}`;
}

/** True when a notification is snoozed past the given moment (epoch ms). */
export function isSnoozed(n, nowMs) {
  return !!(n && typeof n.snoozedUntil === 'number' && n.snoozedUntil > nowMs);
}

/**
 * RW-190: derive a sensible display title for a notification record.
 * Prefers an explicit `title`, then falls back to a prettified `type`
 * (e.g. `buyer_match` → `Buyer Match`), and finally `Notification`.
 * Used by NotificationBell + NotificationsPage so legacy docs that pre-date
 * the title field never render as "(no title)" or "No Title".
 */
export function getNotificationDisplayTitle(notification) {
  if (!notification) return 'Notification';
  if (notification.title && String(notification.title).trim()) {
    return notification.title;
  }
  // Some legacy docs put the headline in postPreview / commentPreview
  if (notification.postPreview && String(notification.postPreview).trim()) {
    const s = String(notification.postPreview).trim();
    return s.length > 80 ? `${s.slice(0, 80)}…` : s;
  }
  const type = notification.type || 'info';
  return String(type)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
